/**
 * Countries: ISO 3166-1 alpha-2 codes, English display names and flags.
 *
 * Flags ship with this package under `assets/country-flags/<code>.svg`
 * (lowercase alpha-2, 4:3), so the host and every widget package share one
 * catalog. Adding a country is adding its file: nothing else to register.
 * The complete upstream 4:3 catalog is bundled, including regional and
 * organization identifiers (small files are inlined by the bundler).
 */

type FlagGlob = (
  pattern: string,
  options: { eager: true; query: "?url"; import: "default" },
) => Record<string, string>;

// `import.meta.glob` is resolved by Vite at build time (app and Vitest). The
// cast types it here without making every package depend on `vite/client`.
const FLAG_FILES = (import.meta as ImportMeta & { glob: FlagGlob }).glob(
  "../../assets/country-flags/*.svg",
  { eager: true, query: "?url", import: "default" },
);

const FLAG_URLS = new Map(
  Object.entries(FLAG_FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf("/") + 1, -".svg".length).toUpperCase(),
    url,
  ]),
);

const ALPHA_2 = /^[A-Za-z]{2}$/;

let regionNames: Intl.DisplayNames | null | undefined;
function regions(): Intl.DisplayNames | null {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = null;
    }
  }
  return regionNames;
}

let codesByName: Map<string, string> | undefined;
function codeForName(name: string): string | undefined {
  if (!codesByName) {
    codesByName = new Map();
    const names = regions();
    for (let first = 65; names && first <= 90; first++) {
      for (let second = 65; second <= 90; second++) {
        const code = String.fromCharCode(first, second);
        const label = names.of(code);
        if (label && label !== code) codesByName.set(label.toLowerCase(), code);
      }
    }
  }
  return codesByName.get(name.toLowerCase());
}

/**
 * The uppercase ISO alpha-2 or bundled flag code for a code or an English country name
 * (`"ml"`, `"ML"`, `"Mali"` → `"ML"`), or `undefined` when unrecognized.
 */
export function countryCode(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (ALPHA_2.test(trimmed) || FLAG_URLS.has(trimmed.toUpperCase())) return trimmed.toUpperCase();
  return codeForName(trimmed);
}

/** English display name of a country code or name; the input itself when unknown. */
export function countryName(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const code = countryCode(trimmed);
  return (code && ALPHA_2.test(code) && regions()?.of(code)) || trimmed;
}

/** URL of the bundled flag for a country code or name, or `undefined` when none ships. */
export function countryFlagUrl(value: string | null | undefined): string | undefined {
  const code = countryCode(value);
  return code ? FLAG_URLS.get(code) : undefined;
}

/** Uppercase codes of every bundled flag, sorted. */
export function availableCountryFlags(): string[] {
  return [...FLAG_URLS.keys()].sort();
}

export const COUNTRY_OPTIONS: readonly { value: string; label: string }[] = availableCountryFlags()
  .filter(
    (code) => ALPHA_2.test(code) && code !== "EU" && code !== "UN" && countryName(code) !== code,
  )
  .map((code) => ({ value: code, label: `${countryName(code)} (${code})` }))
  .sort((left, right) => left.label.localeCompare(right.label, "en"));
